import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JournalFormattedView } from '@/components/journal/JournalFormattedView';
import {
  JournalHistoryModeTabs,
  type JournalHistoryPanel,
} from '@/components/journal/JournalHistoryModeTabs';
import { JournalWritePatientFiles } from '@/components/journal/JournalWritePatientFiles';
import { JournalWritePatientMarkings } from '@/components/journal/JournalWritePatientMarkings';
import { JOURNAL_WRITE_ALL_EMPLOYEES } from '@/lib/journalWritePractitionerFilter';

type FullscreenHistoryNote = {
  id: string;
  title: string | null;
  date: string;
  contentMd: string;
  contentHtml: string | null;
  authorName: string | null;
};

/** Fullscreen editor copy of the notes, files, and markings panel. Opens on notes. */
export function JournalFullscreenHistory({
  clinicId,
  patientId,
  userId,
  notes,
  query,
  onQueryChange,
}: {
  clinicId: string;
  patientId?: string;
  userId?: string | null;
  notes: FullscreenHistoryNote[];
  query: string;
  onQueryChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const tabsId = useId();
  const [panel, setPanel] = useState<JournalHistoryPanel>('notes');
  const [markingSearch, setMarkingSearch] = useState('');

  return (
    <>
      <div className="shrink-0 border-b border-ds-line p-3">
        <p className="mb-2 text-sm font-medium text-ds-text">{t('journal.editor.history_title')}</p>
        <JournalHistoryModeTabs idBase={tabsId} value={panel} onChange={setPanel} />
        {panel === 'notes' ? (
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={t('journal.search_placeholder')}
            className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-2 py-1.5 text-sm text-ds-text"
          />
        ) : panel === 'markings' ? (
          <input
            type="search"
            value={markingSearch}
            onChange={(event) => setMarkingSearch(event.target.value)}
            placeholder={t('journal.history_markings_search')}
            aria-label={t('journal.history_markings_search')}
            className="mt-2 block w-full rounded-md border border-ds-field bg-ds-card px-2 py-1.5 text-sm text-ds-text"
          />
        ) : null}
      </div>
      {panel === 'notes' ? (
        <div
          role="tabpanel"
          id={`${tabsId}-notes-panel`}
          aria-labelledby={`${tabsId}-notes`}
          className="min-h-0 flex-1 overflow-y-auto p-3"
        >
          {notes.length === 0 ? (
            <p className="text-sm text-ds-muted">{t('journal.no_entries')}</p>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <article
                  key={note.id}
                  className="bg-ds-card/60 select-text rounded-md border border-ds-line bg-ds-sunk p-3"
                >
                  <h4 className="text-sm font-semibold text-ds-text">
                    {note.title || t('journal.untitled')}
                  </h4>
                  <p className="mt-0.5 text-xs text-ds-muted">
                    {note.date}
                    {note.authorName ? ` · ${note.authorName}` : ''}
                  </p>
                  <div className="mt-2 text-sm">
                    <JournalFormattedView html={note.contentHtml} text={note.contentMd} />
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      ) : panel === 'files' ? (
        <div
          role="tabpanel"
          id={`${tabsId}-files-panel`}
          aria-labelledby={`${tabsId}-files`}
          className="flex min-h-0 flex-1 flex-col overflow-hidden p-3"
        >
          {patientId ? (
            <JournalWritePatientFiles clinicId={clinicId} patientId={patientId} userId={userId} />
          ) : (
            <p className="text-sm text-ds-muted">{t('patients.files.empty')}</p>
          )}
        </div>
      ) : (
        <div
          role="tabpanel"
          id={`${tabsId}-markings-panel`}
          aria-labelledby={`${tabsId}-markings`}
          className="flex min-h-0 flex-1 flex-col overflow-hidden p-3"
        >
          {patientId ? (
            <JournalWritePatientMarkings
              clinicId={clinicId}
              patientId={patientId}
              practitionerId={JOURNAL_WRITE_ALL_EMPLOYEES}
              search={markingSearch}
            />
          ) : (
            <p className="text-sm text-ds-muted">{t('journal.history_markings_empty')}</p>
          )}
        </div>
      )}
    </>
  );
}
